import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal, Space, Spin, Typography, message } from 'antd';
import { CheckCircleOutlined, CopyOutlined, DownloadOutlined, LinkOutlined, ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { generateOnePayUrlApi } from '@app/api/onepay.api';
import { getOrderApi } from '@app/api/order.api';
import { getOrder } from '@app/store/slices/orderSlice';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import { OrderModel } from '@app/interfaces/order';
import { formatMoney } from '@app/utils/utils';
import { hasOutstandingDebt, outstandingAmount, overpaidAmount } from '@app/utils/orderDebt';
import { DATE_TIME_FORMAT } from '@app/constants/dateFormats';
const { Text, Title } = Typography;

const QR_SIZE = 260;
const POLL_INTERVAL_MS = 5000;
const QR_DOWNLOAD_SIZE = 520;

// OnePay trả expiry dạng "20260609T090005Z" (UTC) → "16:00 09/06/2026" giờ địa phương
const formatExpiry = (raw?: string): string => {
  if (!raw) return '';
  const match = raw.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
  if (!match) return '';
  const utcDate = new Date(
    Date.UTC(+match[1], +match[2] - 1, +match[3], +match[4], +match[5], +match[6]),
  );
  return moment(utcDate).format(DATE_TIME_FORMAT);
};

interface Props {
  open: boolean;
  onCancel: () => void;
  record: OrderModel;
}

const OnePayQRModal: React.FC<Props> = ({ open, onCancel, record }) => {
  const dispatch = useAppDispatch();
  const [paymentUrl, setPaymentUrl] = useState('');
  // qr_url is the short generalv2 URL — much less dense QR than the full vpc URL
  const [qrImgSrc, setQrImgSrc] = useState('');
  const [expiryTime, setExpiryTime] = useState('');
  // Số tiền do backend khoá vào mã QR — nguồn sự thật cho con số hiển thị.
  const [qrAmount, setQrAmount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [paid, setPaid] = useState(false);
  const initialDebitRef = useRef(record.debit_amount);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  // force=true: yêu cầu backend bỏ cache và tạo giao dịch OnePay mới — dùng khi
  // QR cũ đã hết hạn (~1 ngày) và cần gửi lại mã mới cho khách hàng.
  const fetchPaymentUrl = useCallback(
    async (force = false) => {
      setLoading(true);
      setPaymentUrl('');
      setQrImgSrc('');
      setExpiryTime('');
      setQrAmount(null);
      try {
        const res = await generateOnePayUrlApi('Order', Number(record.id), force);
        if (res?.payment_url) {
          setPaymentUrl(res.payment_url);
          setExpiryTime(formatExpiry(res.expiry_time));
          if (typeof res.amount === 'number') setQrAmount(res.amount);
          if (res.qr_image) {
            // EMV VietQR — generated server-side from vpc_DataQr, scannable by banking apps
            setQrImgSrc(res.qr_image);
          }
          // No qr_image → VietQR API failed; don't show a QR at all.
          // Customer can still pay via the "Mở trang thanh toán" button below.
          if (force) {
            message.success('Đã tạo mã QR mới. Gửi lại mã này cho khách hàng.');
          }
        } else {
          message.error('Không nhận được liên kết thanh toán từ OnePay');
        }
      } catch {
        message.error('Không thể tạo liên kết thanh toán OnePay');
      } finally {
        setLoading(false);
      }
    },
    [record.id],
  );

  useEffect(() => {
    if (!open) {
      stopPolling();
      return;
    }
    setPaid(false);
    initialDebitRef.current = record.debit_amount;
    // Đơn đã thu thừa thì backend chắc chắn từ chối tạo mã — hiện cảnh báo, không gọi API.
    if (overpaidAmount(record.debit_amount) === 0) fetchPaymentUrl();

    pollRef.current = setInterval(async () => {
      try {
        const updated = await getOrderApi(Number(record.id));
        // Thu đủ (0) hoặc thu thừa (debit dương) đều là "đã trả xong" — chỉ còn nợ
        // (debit âm) mới tiếp tục chờ.
        if (updated && !hasOutstandingDebt(updated.debit_amount) && initialDebitRef.current !== 0) {
          setPaid(true);
          stopPolling();
          dispatch(getOrder(Number(record.id)));
        }
      } catch {
        // polling errors are silent
      }
    }, POLL_INTERVAL_MS);

    return stopPolling;
  }, [open]);

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(paymentUrl).then(() => {
      message.success('Đã sao chép liên kết thanh toán');
    });
  };

  // Tải QR thành file PNG (convert từ SVG data URI qua canvas) để nhân viên
  // gửi ảnh cho khách qua Zalo/SMS khi mã trên phiếu in đã hết hạn.
  const handleDownloadQr = () => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = QR_DOWNLOAD_SIZE;
      canvas.height = QR_DOWNLOAD_SIZE;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        message.error('Không thể tạo ảnh QR để tải');
        return;
      }
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, QR_DOWNLOAD_SIZE, QR_DOWNLOAD_SIZE);
      ctx.drawImage(img, 20, 20, QR_DOWNLOAD_SIZE - 40, QR_DOWNLOAD_SIZE - 40);
      const link = document.createElement('a');
      link.download = `QR-thanhtoan-${record.order_code}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      message.success('Đã tải ảnh QR');
    };
    img.onerror = () => message.error('Không thể tạo ảnh QR để tải');
    img.src = qrImgSrc;
  };

  // Ưu tiên số tiền backend đã khoá vào mã QR; trước khi có phản hồi thì tạm hiển thị
  // công nợ của đơn. KHÔNG dùng Math.abs(debit_amount): đơn thu thừa có debit dương,
  // lấy trị tuyệt đối sẽ đòi khách trả lại đúng khoản vừa thu thừa (sự cố HD02410).
  const overpaid = overpaidAmount(record.debit_amount);
  const amount = qrAmount ?? outstandingAmount(record.debit_amount);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img
            src="https://onepay.vn/wp-content/uploads/2023/09/logo-onepay.svg"
            alt="OnePay"
            style={{ height: 22 }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          Thanh toán OnePay — {record.order_code}
        </span>
      }
      footer={null}
      width={380}
      centered
      destroyOnClose
      maskClosable={false}
      keyboard={false}
    >
      {paid ? (
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <CheckCircleOutlined className="tw-text-success-teal" style={{ fontSize: 64 }} />
          <Title level={4} style={{ color: 'var(--success-color)', marginTop: 16 }}>
            Thanh toán thành công!
          </Title>
          <Text type="secondary">Đơn hàng đã được cập nhật.</Text>
          <div style={{ marginTop: 20 }}>
            <Button type="primary" onClick={onCancel}>
              Đóng
            </Button>
          </div>
        </div>
      ) : overpaid > 0 ? (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <Title level={4} style={{ color: 'var(--warning-color)' }}>
            Đơn đã thu thừa {formatMoney(overpaid)}
          </Title>
          <Text type="secondary">
            Đơn hàng này đã được thanh toán vượt số phải thu nên không tạo mã QR mới. Vui lòng kiểm tra lại lịch
            sử thanh toán và hoàn phần tiền thừa cho khách.
          </Text>
          <div style={{ marginTop: 20 }}>
            <Button type="primary" onClick={onCancel}>
              Đóng
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ textAlign: 'center' }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
            Số tiền cần thanh toán
          </Text>
          <Title level={3} style={{ color: 'var(--error-color)', margin: '0 0 16px' }}>
            {formatMoney(amount)}
          </Title>

          {loading ? (
            <div style={{ padding: '40px 0' }}>
              <Spin size="large" />
              <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
                Đang tạo mã QR...
              </Text>
            </div>
          ) : qrImgSrc ? (
            <>
              <div
                className="tw-border tw-border-solid tw-border-surface-container-low tw-bg-surface-container-lowest"
                style={{
                  display: 'inline-block',
                  padding: 8,
                  borderRadius: 12,
                  marginBottom: 16,
                }}
              >
                <img
                  src={qrImgSrc}
                  alt="OnePay QR Code"
                  width={QR_SIZE}
                  height={QR_SIZE}
                  style={{ display: 'block', borderRadius: 8 }}
                />
              </div>
              <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>
                Quét mã QR VietQR bằng ứng dụng ngân hàng để thanh toán trực tiếp
              </Text>
              {expiryTime && (
                <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 11 }}>
                  Mã có hiệu lực đến {expiryTime}
                </Text>
              )}
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  icon={<LinkOutlined />}
                  block
                  href={paymentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  type="primary"
                >
                  Mở trang thanh toán
                </Button>
                <Button icon={<CopyOutlined />} block onClick={handleCopyUrl}>
                  Sao chép liên kết
                </Button>
                <Button icon={<DownloadOutlined />} block onClick={handleDownloadQr}>
                  Tải ảnh QR
                </Button>
                <Button icon={<ReloadOutlined />} block onClick={() => fetchPaymentUrl(true)}>
                  Tạo lại mã QR
                </Button>
              </Space>
              <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 11 }}>
                Tự động cập nhật sau khi thanh toán thành công
              </Text>
            </>
          ) : (
            <div style={{ padding: '20px 0' }}>
              <Text type="warning" style={{ display: 'block', marginBottom: 12 }}>
                Không thể tạo mã QR VietQR. Vui lòng thanh toán qua liên kết bên dưới.
              </Text>
              <Space direction="vertical" style={{ width: '100%' }}>
                {paymentUrl && (
                  <Button
                    icon={<LinkOutlined />}
                    block
                    href={paymentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    type="primary"
                  >
                    Mở trang thanh toán OnePay
                  </Button>
                )}
                {paymentUrl && (
                  <Button icon={<CopyOutlined />} block onClick={handleCopyUrl}>
                    Sao chép liên kết
                  </Button>
                )}
                <Button icon={<ReloadOutlined />} block onClick={() => fetchPaymentUrl(true)}>
                  Thử lại
                </Button>
              </Space>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default OnePayQRModal;
